/** Small text helpers shared by several screens. */

/** "CODUL MUNCII" -> "Codul muncii". legis.md mastheads are often capitals. */
function sentenceCase(text: string): string {
  const t = text.trim();
  if (t !== t.toLocaleUpperCase("ro") || !/\p{L}/u.test(t)) return t;
  const lower = t.toLocaleLowerCase("ro");
  return lower.charAt(0).toLocaleUpperCase("ro") + lower.slice(1);
}

/**
 * An act's title as a chip or heading reads it: "CODUL MUNCII AL REPUBLICII
 * MOLDOVA" -> "Codul muncii". A title that starts mid-phrase ("privind
 * contractele de credit…", the act type being stored apart) gets a capital.
 * The asterisk legis.md puts after a republished code goes too: left on,
 * it kept the suffix from matching ("…al republicii moldova*").
 */
export function shortAct(title: string): string {
  const t = sentenceCase(title.replace(/\s*\*+\s*$/, ""))
    .replace(/\s+al Republicii Moldova\s*$/i, "")
    .trim();
  return t.charAt(0).toLocaleUpperCase("ro") + t.slice(1);
}

const ACT_TYPE: Record<string, string> = {
  LEGE: "Legea",
  "LEGE ORGANICĂ": "Legea organică",
  HOTĂRÂRE: "Hotărârea",
  HOTARARE: "Hotărârea",
  DECRET: "Decretul",
  ORDIN: "Ordinul",
  REGULAMENT: "Regulamentul",
};

/**
 * The act's name with its type and number, for the source panel heading:
 * "Legea nr. 202 privind contractele de credit…", "Codul muncii nr. 154".
 * The type is only prefixed when the title does not already start with it.
 */
export function actName(
  actType: string | null,
  number: string | null,
  title: string,
): string {
  const t = sentenceCase(title).replace(/\s+al Republicii Moldova\s*$/i, "").trim();
  const nr = number ? ` nr. ${number}` : "";
  const startsMid = /^\p{Ll}/u.test(t);
  if (startsMid && actType) {
    const type = ACT_TYPE[actType.toUpperCase()] ?? sentenceCase(actType);
    return `${type}${nr} ${t}`;
  }
  return shortAct(t) + nr;
}

/**
 * "/capitolul-iv/articolul-15/alin-1/pct-b" -> "art. 15 alin. (1) lit. b)".
 * The address a lawyer writes, from the path a citation carries. A
 * Government decision is numbered by points, not articles: "/pct-6" ->
 * "pct. 6". Null when the path names neither.
 */
export function pathRef(path: string | null): string | null {
  if (!path) return null;
  const out: string[] = [];
  for (const seg of path.split("/")) {
    let m: RegExpExecArray | null;
    if ((m = /^articolul-(.+)$/.exec(seg))) out.push(`art. ${m[1]}`);
    else if ((m = /^alin-(.+)$/.exec(seg))) out.push(`alin. (${m[1]})`);
    else if ((m = /^(?:pct|lit)-(.+)$/.exec(seg)))
      out.push(/^\d+$/.test(m[1]) ? `pct. ${m[1]}` : `lit. ${m[1]})`);
  }
  return out.length && /^(?:art|pct)\. /.test(out[0]) ? out.join(" ") : null;
}

/** "Articolul 86, alin. (2)" -> "art. 86 alin. (2)". The form lawyers write. */
export function shortLabel(label: string): string {
  return label
    .replace(/^Articolul\s+/i, "art. ")
    .replace(/,\s*/g, " ")
    .trim();
}

/** "2026-08-20" -> "20.08.2026", the way Moldovan courts date a decision. */
export function courtDate(iso: string | null | undefined): string {
  const [y, m, d] = (iso ?? "").split("-");
  return d ? `${d}.${m}.${y}` : iso ?? "";
}

/** A cited decision as a chip names it: "CSJ 1ra-1047/2024 · 20.08.2026". */
export function caseRef(c: { case_number?: string | null; label: string;
                             decided_on?: string | null }): string {
  const date = courtDate(c.decided_on);
  return `CSJ ${c.case_number || c.label}` + (date ? ` · ${date}` : "");
}

/** A conversation or draft time, relative while recent. */
export function when(iso: string): string {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) {
    const mins = Math.floor((Date.now() - d.getTime()) / 60_000);
    if (mins < 2) return "acum";
    return "azi, " + d.toLocaleTimeString("ro-MD", { hour: "2-digit", minute: "2-digit" });
  }
  if (days === 1) return "ieri";
  if (days < 7) return `acum ${days} zile`;
  return d.toLocaleDateString("ro-MD", { day: "numeric", month: "short" });
}

/** "1 câmp lipsă" / "4 câmpuri lipsă". */
export function missingFields(n: number): string {
  return n === 1 ? "1 câmp lipsă" : `${n} câmpuri lipsă`;
}

/** "1 obligație" / "3 obligații". */
export function obligations(n: number): string {
  return n === 1 ? "1 obligație" : `${n} obligații`;
}
